import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatMoney } from "../lib/api";
import { useApp } from "../context/AppContext";
import { CreditCard, QrCode, Building2, Smartphone, Truck, Loader2 } from "lucide-react";

const paymentMethods = [
  { id: "qris", label_id: "QRIS", label_en: "QRIS", icon: QrCode, sub: "GoPay, OVO, Dana, ShopeePay, Mobile Banking" },
  { id: "bank_transfer", label_id: "Transfer Bank", label_en: "Bank Transfer", icon: Building2, sub: "BCA, Mandiri, BNI, BRI, Permata" },
  { id: "credit_card", label_id: "Kartu Kredit", label_en: "Credit Card", icon: CreditCard, sub: "Visa, Mastercard, JCB" },
  { id: "gopay", label_id: "GoPay", label_en: "GoPay", icon: Smartphone, sub: "" },
  { id: "shopeepay", label_id: "ShopeePay", label_en: "ShopeePay", icon: Smartphone, sub: "" },
  { id: "dana", label_id: "DANA", label_en: "DANA", icon: Smartphone, sub: "" },
];

// Dynamically load Midtrans Snap.js (once) and set client key
const loadSnapScript = (snapUrl, clientKey) =>
  new Promise((resolve, reject) => {
    if (document.querySelector(`script[data-midtrans-snap="1"]`)) return resolve();
    const s = document.createElement("script");
    s.src = snapUrl;
    s.async = true;
    s.dataset.midtransSnap = "1";
    s.setAttribute("data-client-key", clientKey);
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Failed to load Snap.js"));
    document.head.appendChild(s);
  });

const Checkout = () => {
  const { cart, cartSubtotalIdr, cartSubtotalUsd, cartWeight, currency, t, lang, clearCart, customer } = useApp();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    customer_name: customer?.name || "",
    customer_email: customer?.email || "",
    customer_phone: "",
    address: "", city: "", province: "", postal_code: "",
    country: lang === "id" ? "Indonesia" : "Indonesia",
    notes: "",
  });
  const [rates, setRates] = useState([]);
  const [shipping, setShipping] = useState(null);
  const [payment, setPayment] = useState("qris");
  const [loadingRates, setLoadingRates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null); // {code, discount_idr, discount_usd, label_id, label_en}
  const [promoError, setPromoError] = useState("");
  const [applyingPromo, setApplyingPromo] = useState(false);

  useEffect(() => {
    if (!cart.length) navigate("/cart");
  }, [cart.length, navigate]);

  const fetchRates = async () => {
    if (!form.country || !form.city) return;
    setLoadingRates(true);
    try {
      const { data } = await api.post("/shipping/rates", {
        destination_country: form.country,
        destination_city: form.city,
        destination_postal_code: parseInt(form.postal_code || "0", 10) || 0,
        weight_grams: cartWeight,
        items: cart.map((it) => ({
          name: it.name,
          value: it.price_idr,
          weight: (it.weight_grams || 200) * (it.bundle_qty || 1),
          quantity: it.quantity,
        })),
        currency,
      });
      setRates(data.rates || []);
      setShipping(data.rates?.[0] || null);
    } finally {
      setLoadingRates(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyPromo = async () => {
    setPromoError("");
    if (!promoCode.trim()) return;
    setApplyingPromo(true);
    try {
      const { data } = await api.post("/promos/validate", {
        code: promoCode.trim(),
        subtotal_idr: cartSubtotalIdr,
      });
      setPromoApplied(data);
    } catch (e) {
      setPromoApplied(null);
      setPromoError(e?.response?.data?.detail || "Promo error");
    } finally { setApplyingPromo(false); }
  };

  const removePromo = () => { setPromoApplied(null); setPromoCode(""); setPromoError(""); };

  const placeOrder = async () => {
    setFormError("");
    const missing = [];
    if (!form.customer_name) missing.push(lang === "id" ? "Nama" : "Name");
    if (!form.customer_email) missing.push("Email");
    if (!form.customer_phone) missing.push(lang === "id" ? "Telepon" : "Phone");
    if (!form.address) missing.push(lang === "id" ? "Alamat" : "Address");
    if (!form.city) missing.push(lang === "id" ? "Kota" : "City");
    if (!shipping) missing.push(lang === "id" ? "Pilih jasa kirim" : "Select courier");
    if (missing.length) {
      setFormError((lang === "id" ? "Lengkapi: " : "Missing: ") + missing.join(", "));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setSubmitting(true);
    try {
      const items = cart.map((it) => ({
        product_id: it.product_id, quantity: it.quantity, bundle_qty: it.bundle_qty || 0,
        price_idr: it.price_idr, price_usd: it.price_usd,
        name: it.name, image: it.image,
      }));
      const { data: order } = await api.post("/orders", {
        ...form, items,
        shipping_courier: shipping.courier,
        shipping_service: shipping.service,
        shipping_cost_idr: shipping.cost_idr,
        shipping_cost_usd: shipping.cost_usd,
        payment_method: payment,
        currency,
        promo_code: promoApplied?.code || "",
        discount_idr: promoApplied?.discount_idr || 0,
        discount_usd: promoApplied?.discount_usd || 0,
      });
      // Get Midtrans public config & load Snap.js if not yet loaded
      const { data: cfg } = await api.get("/payment/config");
      await loadSnapScript(cfg.snap_url, cfg.client_key);
      // Create Snap transaction
      const { data: snap } = await api.post("/payment/create", { order_id: order.id, method: payment });
      clearCart();
      // Invoke Midtrans Snap popup
      if (window.snap && snap.snap_token) {
        window.snap.pay(snap.snap_token, {
          onSuccess: () => navigate(`/order/${order.id}`),
          onPending: () => navigate(`/order/${order.id}`),
          onError: () => navigate(`/order/${order.id}`),
          onClose: () => navigate(`/order/${order.id}`),
        });
      } else if (snap.redirect_url) {
        window.location.href = snap.redirect_url;
      } else {
        navigate(`/order/${order.id}`);
      }
    } catch (e) {
      setFormError(e?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const shippingIdr = shipping?.cost_idr || 0;
  const shippingUsd = shipping?.cost_usd || 0;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
      <h1 className="font-serif text-4xl text-holy-red mb-8">{t("checkout")}</h1>
      {formError && (
        <div className="mb-6 rounded-xl border border-holy-red bg-holy-red/5 px-4 py-3 text-sm text-holy-red" data-testid="checkout-error">
          ⚠️ {formError}
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          {/* Customer Info */}
          <section className="bg-white rounded-2xl p-6 border border-border/60">
            <h3 className="font-serif text-xl mb-4">1. {t("customer_info")}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="cust-name" placeholder={t("full_name")} value={form.customer_name} onChange={(e) => update("customer_name", e.target.value)} className="input-base" />
              <input data-testid="cust-email" placeholder={t("email")} value={form.customer_email} onChange={(e) => update("customer_email", e.target.value)} className="input-base" />
              <input data-testid="cust-phone" placeholder={t("phone")} value={form.customer_phone} onChange={(e) => update("customer_phone", e.target.value)} className="input-base" />
              <input data-testid="cust-country" placeholder={t("country")} value={form.country} onChange={(e) => update("country", e.target.value)} className="input-base" />
              <input data-testid="cust-address" placeholder={t("address")} value={form.address} onChange={(e) => update("address", e.target.value)} className="input-base sm:col-span-2" />
              <input data-testid="cust-city" placeholder={t("city")} value={form.city} onChange={(e) => update("city", e.target.value)} className="input-base" />
              <input data-testid="cust-province" placeholder={t("province")} value={form.province} onChange={(e) => update("province", e.target.value)} className="input-base" />
              <input data-testid="cust-postal" placeholder={t("postal_code")} value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className="input-base" />
              <input data-testid="cust-notes" placeholder={t("notes")} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="input-base sm:col-span-2" />
            </div>
            <button onClick={fetchRates} disabled={loadingRates || !form.city} className="mt-4 inline-flex items-center gap-2 bg-holy-green hover:bg-holy-green-dark text-ivory rounded-full px-5 py-2 text-sm disabled:opacity-50" data-testid="fetch-rates-btn">
              {loadingRates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
              {lang === "id" ? "Hitung Ongkos Kirim" : "Calculate Shipping"}
            </button>
          </section>

          {/* Shipping */}
          {rates.length > 0 && (
            <section className="bg-white rounded-2xl p-6 border border-border/60">
              <h3 className="font-serif text-xl mb-4">2. {t("select_courier")}</h3>
              <div className="space-y-2">
                {rates.map((r, i) => {
                  const sel = shipping?.courier === r.courier && shipping?.service === r.service;
                  return (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setShipping(r)}
                      data-testid={`rate-${r.courier}-${r.service}`}
                      className={`w-full text-left flex items-center gap-4 rounded-xl border p-4 cursor-pointer transition-all ${sel ? "border-holy-red bg-holy-red/5" : "border-border hover:border-holy-red/50 bg-white"}`}
                    >
                      <span className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all ${sel ? "border-holy-red bg-holy-red ring-4 ring-holy-red/20" : "border-border"}`} />
                      <div className="flex-1">
                        <div className="font-medium">{r.courier} <span className="text-muted-foreground text-sm">· {r.service}</span></div>
                        <div className="text-xs text-muted-foreground">{r.etd}</div>
                      </div>
                      <div className="text-holy-red font-semibold">{formatMoney(r.cost_idr, r.cost_usd, currency)}</div>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          {/* Payment */}
          <section className="bg-white rounded-2xl p-6 border border-border/60">
            <h3 className="font-serif text-xl mb-4">3. {t("select_payment")}</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {paymentMethods.map((m) => {
                const Icon = m.icon;
                const sel = payment === m.id;
                return (
                  <button
                    type="button"
                    key={m.id}
                    onClick={() => setPayment(m.id)}
                    data-testid={`pay-${m.id}`}
                    className={`text-left flex items-center gap-3 rounded-xl border p-4 cursor-pointer transition-all ${sel ? "border-holy-red bg-holy-red/5" : "border-border hover:border-holy-red/50 bg-white"}`}
                  >
                    <span className={`w-4 h-4 rounded-full border-2 shrink-0 transition-all ${sel ? "border-holy-red bg-holy-red ring-4 ring-holy-red/20" : "border-border"}`} />
                    <Icon className="w-5 h-5 text-holy-red" />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{lang === "id" ? m.label_id : m.label_en}</div>
                      {m.sub && <div className="text-xs text-muted-foreground">{m.sub}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        {/* Summary */}
        <aside className="bg-cream rounded-2xl p-6 h-fit sticky top-28">
          <h3 className="font-serif text-xl mb-4">Order Summary</h3>
          <div className="space-y-2 mb-4">
            {cart.map((it) => (
              <div key={it.product_id} className="flex justify-between text-xs">
                <span className="truncate pr-2">{it.name} × {it.quantity}</span>
                <span className="shrink-0">{formatMoney(it.price_idr * it.quantity, it.price_usd * it.quantity, currency)}</span>
              </div>
            ))}
          </div>

          {/* Promo code input */}
          <div className="border-t border-border pt-3 mt-3">
            {!promoApplied ? (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">{lang === "id" ? "Kode Promo" : "Promo Code"}</div>
                <div className="flex gap-2">
                  <input
                    data-testid="promo-input"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="NATAL2026"
                    className="flex-1 bg-white border border-border rounded-full px-4 py-2 text-sm focus:border-holy-red outline-none uppercase"
                  />
                  <button data-testid="promo-apply" type="button" onClick={applyPromo} disabled={applyingPromo || !promoCode.trim()} className="bg-holy-red text-ivory rounded-full px-4 text-xs disabled:opacity-50">
                    {applyingPromo ? "..." : (lang === "id" ? "Pakai" : "Apply")}
                  </button>
                </div>
                {promoError && <div className="text-xs text-holy-red mt-1" data-testid="promo-error">{promoError}</div>}
              </div>
            ) : (
              <div className="bg-holy-green/10 border border-holy-green/30 rounded-xl p-3 flex items-center justify-between" data-testid="promo-applied">
                <div>
                  <div className="text-xs font-semibold text-holy-green">✓ {promoApplied.code}</div>
                  <div className="text-[11px] text-muted-foreground">{lang === "id" ? promoApplied.label_id : promoApplied.label_en}</div>
                </div>
                <button onClick={removePromo} className="text-xs text-holy-red hover:underline" data-testid="promo-remove">{lang === "id" ? "Hapus" : "Remove"}</button>
              </div>
            )}
          </div>

          <hr className="border-border my-3" />
          <div className="flex justify-between text-sm py-1"><span>{t("subtotal")}</span><span>{formatMoney(cartSubtotalIdr, cartSubtotalUsd, currency)}</span></div>
          {promoApplied && (
            <div className="flex justify-between text-sm py-1 text-holy-green"><span>{lang === "id" ? "Diskon" : "Discount"}</span><span>-{formatMoney(promoApplied.discount_idr, promoApplied.discount_usd, currency)}</span></div>
          )}
          <div className="flex justify-between text-sm py-1"><span>{t("shipping")}</span><span>{shipping ? formatMoney(shippingIdr, shippingUsd, currency) : "—"}</span></div>
          <hr className="border-border my-3" />
          <div className="flex justify-between text-lg font-semibold py-1"><span>{t("total")}</span>
            <span className="text-holy-red" data-testid="order-total">
              {formatMoney(
                cartSubtotalIdr - (promoApplied?.discount_idr || 0) + shippingIdr,
                cartSubtotalUsd - (promoApplied?.discount_usd || 0) + shippingUsd,
                currency
              )}
            </span>
          </div>
          <button
            data-testid="place-order-btn"
            onClick={placeOrder}
            disabled={submitting || !shipping}
            className="w-full bg-holy-red hover:bg-holy-red-dark text-ivory rounded-full py-3.5 mt-5 disabled:opacity-50 transition-all hover:-translate-y-0.5"
          >
            {submitting ? "..." : t("place_order")}
          </button>
        </aside>
      </div>

      <style>{`.input-base{background:#fff;border:1px solid hsl(var(--border));padding:.75rem 1rem;border-radius:.75rem;font-size:.875rem;outline:none;width:100%}.input-base:focus{border-color:#9B1C26;box-shadow:0 0 0 3px rgba(155,28,38,.1)}`}</style>
    </div>
  );
};

export default Checkout;
